import { locales } from '@/locales';
import { Footer as FooterComponent, Icon } from '@bumble/ui-kit';
import common from 'components/lexemes/common';
import footer from 'components/lexemes/footer';
import header from 'components/lexemes/header';
import { CONTACT_US_URL, SUPPORT_URL } from 'lib/constants';
import { Country, isEuCountry } from 'lib/enums/country';
import { useGlobalProps } from 'lib/hooks/use-global-props';
import { useRouter } from 'next/router';

export default function Footer() {
  const globalProps = useGlobalProps();
  const country = globalProps?.country;

  const router = useRouter();
  const lang = router.locale!.indexOf('_') !== -1 ? router.locale!.split('_')[0] : router.locale;

  function onLanguageChange(event: any) {
    const locale = event;
    let newRoute = `/love-stories/${locale}/${router.asPath}`;
    newRoute = newRoute.replace('//', '/');
    // @ts-ignore
    window.location = newRoute;
    // router.push(`/${router.asPath}`, undefined, { locale: locale });
  }

  const appLinks = {
    title: footer.lxFooterAppsTitle(),
    links: [
      {
        text: footer.lxFooterItemBumbleDate(),
        url: `/${lang}/date`,
      },
      { text: 'BFF', url: `/${lang}/bff` },
      { text: 'Badoo', url: 'https://badoo.com', hasIcon: true },
    ],
  };
  const companyLinks = {
    title: footer.lxFooterCompanyTitle(),
    links: [
      { text: footer.lxFooterItemAbout(), url: `/${lang}/about` },
      { text: footer.lxFooterItemContactUs(), url: CONTACT_US_URL },
      {
        text: footer.lxFooterItemCareers(),
        url: 'https://team.bumble.com',
        hasIcon: true,
      },
      {
        text: footer.lxFooterItemInvestors(),
        url: 'https://ir.bumble.com',
        hasIcon: true,
      },
      {
        text: footer.lxFooterItemShop(),
        url: 'https://bumble.shop',
        hasIcon: true,
      },
      {
        text: header.lxHeaderItemLabelBuzz,
        url: '/the-buzz',
        hasIcon: true,
      },
      { text: footer.lxFooterItemSupport(), url: SUPPORT_URL },
    ],
  };
  const legalLinks = {
    title: footer.lxFooterLegalTitle(),
    links: [
      { text: footer.lxFooterItemGuidelines(), url: `/${lang}/guidelines` },
      country === Country.USA
        ? {
            text: footer.lxDoNotSell(),
            onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
              event.preventDefault();

              // CCPA Privacy Manager Call
              try {
                //@ts-ignore - describe the _sp_ object later
                window._sp_.ccpa.loadPrivacyManagerModal('708248');
              } catch (error) {
                window.location.href = '/cookie-policy';
              }
            },
          }
        : {
            text: footer.lxFooterItemManageCookies(),
            url: '/',
            onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
              event.preventDefault();
              // GDPR Privacy Manager Call - later list only GDPR countries
              try {
                // @ts-ignore - describe the _sp_ object later
                window._sp_.gdpr.loadPrivacyManagerModal();
              } catch (error) {
                window.location.href = '/cookie-policy';
              }
            },
          },
      country === Country.GERMANY
        ? {
            text: footer.lxGermanCancelationPolicyFlow(),
            url: `/${lang}/help/cancelation-flow`,
          }
        : null,
      {
        text: footer.lxFooterItemPrivacyPolicy(),
        url: lang ? `/privacy-policy/${lang}` : '/privacy-policy',
      },
      { text: footer.lxFooterItemTerms(), url: `/${lang}/terms` },
      {
        text: footer.lxFooterItemA11y(),
        url: isEuCountry(country) ? `/eaa-accessibility-statement` : `/${lang}/accessibility`,
      },
      {
        text: footer.lxFooterItemNoticeAtCollection(),
        url: '/notice-at-collection',
      },
      {
        text: footer.lxFooterItemModernSlaveryAct(),
        url: '/modern-slavery-act-statement',
      },
      {
        text: footer.lxFooterItemWashingtonConsumerHealthDataPrivacyPolicy(),
        url: '/bumble-washington-consumer-health-data-privacy-policy',
      },
    ].filter(Boolean),
  };

  const socialLinks = [
    {
      url: 'https://www.instagram.com/bumble',
      a11yLabel: `${common.lxA11ySocialLink('Instagram')} ${common.lxA11yOpensInNewWindow}`,
      iconName: Icon.Name.Instagram,
    },
    {
      url: 'https://www.facebook.com/bumbleapp',
      a11yLabel: `${common.lxA11ySocialLink('Facebook')} ${common.lxA11yOpensInNewWindow}`,
      iconName: Icon.Name.Facebook,
    },
    {
      url: 'https://www.twitter.com/bumble',
      a11yLabel: `${common.lxA11ySocialLink('Twitter')} ${common.lxA11yOpensInNewWindow}`,
      iconName: Icon.Name.Twitter,
    },
    {
      url: 'https://www.linkedin.com/company/bumbleinc',
      a11yLabel: `${common.lxA11ySocialLink('LinkedIn')} ${common.lxA11yOpensInNewWindow}`,
      iconName: Icon.Name.Linkedin,
    },
    {
      url: 'https://www.pinterest.com/bumbleapp',
      a11yLabel: `${common.lxA11ySocialLink('Pinterest')} ${common.lxA11yOpensInNewWindow}`,
      iconName: Icon.Name.Pinterest,
    },
    {
      url: 'https://www.youtube.com/bumble',
      a11yLabel: `${common.lxA11ySocialLink('YouTube')} ${common.lxA11yOpensInNewWindow}`,
      iconName: Icon.Name.Youtube,
    },
    {
      url: 'https://www.tiktok.com/@bumble',
      a11yLabel: `${common.lxA11ySocialLink('TikTok')} ${common.lxA11yOpensInNewWindow}`,
      iconName: Icon.Name.Tiktok,
    },
  ];

  return (
    <FooterComponent
      appLinks={appLinks}
      companyLinks={companyLinks}
      downloadWidgetText={footer.lxFooterAppDownload()}
      downloadCtaText={footer.lxFooterDownloadTheApp()}
      downloadUrl="https://bumble.onelink.me/3396940749/yuhjm82c"
      languages={locales}
      // @ts-ignore
      legalLinks={legalLinks}
      socialLinks={socialLinks}
      onLanguageChange={onLanguageChange}
      lang={router.locale}
    />
  );
}
